import type { FC } from 'react';
import { vegaCopyConversation } from '@gonfalon/dogfood-flags/dogfood-flags';
import { DateFormat } from '@gonfalon/format/constants';
import { format } from '@gonfalon/format/format';
import { msg } from '@gonfalon/messages/msg';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';

import { useAgentInsights } from '../../../../../AgentInsightsContext';
import { extractResultText } from '../../../../../agentInsightsUtils';
import { ConversationMessageCard } from '../../../../../components/conversation/ConversationMessageCard';
import { trackVegaAiMessageCopied } from '../../../../../vegaAnalytics';
import { useAgentResponseSelector } from '../../hooks/useAgentResponseSelector';
import { VegaMarkdownRenderer } from '../artifacts/VegaMarkdownRenderer';
import { CopyButton } from '../CopyButton';

import { copyHoverGroup } from '../../../conversation/ConversationChrome.css';

interface ResultMessageProps {
  message: any;
  timestamp?: string;
  model?: string;
  onSetAgentResponse?: (response: string) => void;
}

// Component for rendering result messages
// these are final analysis results from the agent
export const ResultMessage: FC<ResultMessageProps> = ({ message, timestamp, model, onSetAgentResponse }) => {
  const { projectKey } = useProjectId();
  const { startConversationWithSource } = useAgentInsights();
  const { onTaskAction } = useAgentResponseSelector(onSetAgentResponse);
  const copyEnabled = vegaCopyConversation();

  const resultText = extractResultText(message.text);
  const formattedTimestamp = timestamp ? format(timestamp, DateFormat.MMM_D_H_MM_A) : null;
  const meta = formattedTimestamp ? [{ key: 'time', value: formattedTimestamp }] : undefined;
  const hasCaption = Boolean(meta) || copyEnabled;

  if (!resultText) {
    return null;
  }

  return (
    <ConversationMessageCard
      speaker="agent"
      variant="chat"
      model={model}
      meta={meta}
      className={hasCaption ? copyHoverGroup : undefined}
      hoverRevealCaption={hasCaption}
      footer={
        copyEnabled ? (
          <CopyButton
            text={resultText}
            label={msg('vega.conversation.copyReplyLabel')}
            successTitle={msg('vega.conversation.copyReplySuccess')}
            onCopy={() => trackVegaAiMessageCopied('vega')}
          />
        ) : undefined
      }
    >
      <VegaMarkdownRenderer
        text={resultText}
        projectKey={projectKey}
        onExternalSourceClick={(match) => startConversationWithSource(match.url, match.sourceType)}
        onTaskAction={onSetAgentResponse ? onTaskAction : undefined}
      />
    </ConversationMessageCard>
  );
};
